<template>
  <button
    type="submit"
    v-bind="filteredAttrs"
    :disabled="disabled || submitting"
    :class="[{ submitting: submitting }, cssClass || '']"
    @click="callback($event)"
  >
    <loading-spinner
      v-if="submitting"
      :key="`spinner-${$attrs['data-test'] || 'unknown'}-${String($route?.name || 'unknown')}`"
      class="spinner"
      :stroke-color="'rgba(255, 255, 255, 0.4)'"
      location="base-submit-button"
    />
    <slot v-if="submitting" name="submittingText">
      {{ $t('Submitting') }}
    </slot>
    <slot v-else>
      {{ $t('Submit') }}
    </slot>
  </button>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'

@Component({
  emits: ['click'],
})
class BaseSubmitButton extends Vue {
  @Prop({ default: false })
  submitting: boolean

  @Prop()
  disabled: boolean

  @Prop({ required: false })
  cssClass?: string

  private _isUnmounting = false

  @Watch('submitting')
  onSubmittingChanged(newVal: boolean, oldVal: boolean) {
    // Prevent race conditions during component unmount
    if (this._isUnmounting || (this.$el && !this.$el.isConnected)) {
      return
    }
  }

  beforeUpdate() {
    // Prevent updates if component is unmounting or DOM is disconnected
    if (this._isUnmounting || (this.$el && !this.$el.isConnected)) {
      // Return false doesn't work in Vue 3, we need to prevent the update differently
      // Skip the update by not allowing the component to re-render
      this.$forceUpdate = () => {}
      return
    }
  }

  beforeUnmount() {
    this._isUnmounting = true
  }

  callback(e: any) {
    this.$emit('click', e)
  }

  get filteredAttrs() {
    const { onClick, ...attrs } = this.$attrs
    return attrs
  }
}

export default toNative(BaseSubmitButton)
</script>

<style scoped>
button.submitting {
  cursor: not-allowed;
  pointer-events: none;
  padding-left: 1em;
}

button[disabled] {
  pointer-events: none;
  cursor: not-allowed;
  color: var(--colour-dark-green);
  background: #8da78f;
  box-shadow: 0 0.375em #7a8a7c;
}

button {
  position: relative;
}

.spinner {
  position: absolute;
  left: 0.8em;
  top: 0.6em;
}
</style>
